import type {
  ApiKey,
  Asset,
  AssetPage,
  AuthStatus,
  Capabilities,
  ChatThread,
  CreateJobInput,
  CreatedApiKey,
  CreateRunnerBody,
  CreateSkillInput,
  CreateUserInput,
  CreateVoiceInput,
  Job,
  JobStat,
  JobStatus,
  Me,
  MediaKind,
  PromptEnhanceRequest,
  PromptEnhanceResponse,
  RunnerInfo,
  RunnerWithToken,
  Skill,
  StatsLive,
  StatsRange,
  StatsSummary,
  TelemetrySeries,
  UpdateRunnerBody,
  UpdateUserBody,
  UpdateVoiceBody,
  User,
  UserWithPassword,
  Voice,
} from "@studio/shared";
import type { UIMessage } from "ai";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Set by the app shell: what to do when the session is gone (go to the sign-in page). */
let onSignedOut: (() => void) | null = null;
export function setSignedOutHandler(handler: () => void) {
  onSignedOut = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init?.body && !(init.body instanceof FormData) ? { "Content-Type": "application/json", ...init.headers } : init?.headers,
  });
  if (!response.ok) {
    let message = `The server answered ${response.status}.`;
    try {
      const body = await response.json();
      message = body.error ?? body.message ?? message;
    } catch {
      /* not JSON */
    }
    if (response.status === 401 && !path.startsWith("/api/auth")) onSignedOut?.();
    throw new ApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "" && value !== false) search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** Multipart upload with progress (fetch cannot report upload progress). */
function upload(file: File, onProgress?: (fraction: number) => void): Promise<Asset> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/uploads");
    xhr.responseType = "json";
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as Asset);
      else {
        if (xhr.status === 401) onSignedOut?.();
        reject(new ApiError(xhr.response?.error ?? `Upload failed (${xhr.status}).`, xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError("Upload failed: the server could not be reached.", 0));
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}

export const api = {
  // ── who you are ──
  authStatus: () => request<AuthStatus>("/api/auth/status"),
  setup: (body: { username: string; password: string; displayName?: string }) =>
    request<Me>("/api/auth/setup", { method: "POST", body: JSON.stringify(body) }),
  login: (username: string, password: string) =>
    request<Me>("/api/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  me: () => request<Me>("/api/me"),
  updateMe: (displayName: string) => request<Me>("/api/me", { method: "PATCH", body: JSON.stringify({ displayName }) }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("/api/me/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }),
  apiKeys: () => request<ApiKey[]>("/api/me/api-keys"),
  createApiKey: (name: string) => request<CreatedApiKey>("/api/me/api-keys", { method: "POST", body: JSON.stringify({ name }) }),
  revokeApiKey: (id: string) => request<ApiKey>(`/api/me/api-keys/${id}`, { method: "DELETE" }),

  capabilities: () => request<Capabilities>("/api/capabilities"),

  assets: (params: { kind?: MediaKind; cursor?: string | null; q?: string; favorite?: boolean }) =>
    request<AssetPage>(`/api/assets${query(params)}`),
  asset: (id: string) => request<Asset>(`/api/assets/${id}`),
  peaks: (id: string) => request<number[]>(`/api/assets/${id}/peaks`),
  upload,
  setFavorite: (id: string, favorite: boolean) =>
    request<Asset>(`/api/assets/${id}`, { method: "PATCH", body: JSON.stringify({ favorite }) }),
  deleteAsset: (id: string) => request<void>(`/api/assets/${id}`, { method: "DELETE" }),
  grabFrame: (id: string, timeSec: number) =>
    request<Asset>(`/api/assets/${id}/frame`, { method: "POST", body: JSON.stringify({ timeSec }) }),

  enhancePrompt: (body: PromptEnhanceRequest) =>
    request<PromptEnhanceResponse>("/api/prompt-enhance", { method: "POST", body: JSON.stringify(body) }),

  createJob: (body: CreateJobInput) => request<Job>("/api/jobs", { method: "POST", body: JSON.stringify(body) }),
  jobs: (params: { status?: JobStatus; chatId?: string } = {}) => request<Job[]>(`/api/jobs${query(params)}`),
  job: (id: string) => request<Job>(`/api/jobs/${id}`),
  startJob: (id: string) => request<Job>(`/api/jobs/${id}/start`, { method: "POST" }),
  cancelJob: (id: string) => request<Job>(`/api/jobs/${id}/cancel`, { method: "POST" }),

  // ── saved voices (the page shows a plain list, so the envelope is unwrapped here) ──
  voices: () => request<{ items: Voice[] }>("/api/voices").then((page) => page.items),
  createVoice: (body: CreateVoiceInput) => request<Voice>("/api/voices", { method: "POST", body: JSON.stringify(body) }),
  updateVoice: (id: string, body: UpdateVoiceBody) =>
    request<Voice>(`/api/voices/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteVoice: (id: string) => request<void>(`/api/voices/${id}`, { method: "DELETE" }),

  chats: () => request<ChatThread[]>("/api/chats"),
  createChat: () => request<ChatThread>("/api/chats", { method: "POST", body: "{}" }),
  chat: (id: string) => request<{ thread: ChatThread; messages: UIMessage[] }>(`/api/chats/${id}`),
  renameChat: (id: string, title: string) =>
    request<ChatThread>(`/api/chats/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),
  deleteChat: (id: string) => request<void>(`/api/chats/${id}`, { method: "DELETE" }),

  // ── activity ──
  statsLive: () => request<StatsLive>("/api/stats/live"),
  statsSummary: (range: StatsRange) => request<StatsSummary>(`/api/stats/summary${query({ range })}`),
  statsJobs: (limit = 40) => request<JobStat[]>(`/api/stats/jobs${query({ limit })}`),
  telemetry: (minutes = 60) => request<TelemetrySeries>(`/api/stats/telemetry${query({ minutes })}`),

  // ── admin ──
  users: () => request<User[]>("/api/admin/users"),
  createUser: (body: CreateUserInput) => request<UserWithPassword>("/api/admin/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (id: string, body: UpdateUserBody) =>
    request<UserWithPassword>(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteUser: (id: string) => request<void>(`/api/admin/users/${id}`, { method: "DELETE" }),
  adminRunners: () => request<RunnerInfo[]>("/api/admin/runners"),
  createRunner: (body: CreateRunnerBody) =>
    request<RunnerWithToken>("/api/admin/runners", { method: "POST", body: JSON.stringify(body) }),
  updateRunner: (id: string, body: UpdateRunnerBody) =>
    request<RunnerWithToken>(`/api/admin/runners/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteRunner: (id: string) => request<void>(`/api/admin/runners/${id}`, { method: "DELETE" }),
  skills: () => request<Skill[]>("/api/admin/skills"),
  createSkill: (body: CreateSkillInput) => request<Skill>("/api/admin/skills", { method: "POST", body: JSON.stringify(body) }),
  updateSkill: (id: string, body: Partial<CreateSkillInput>) =>
    request<Skill>(`/api/admin/skills/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteSkill: (id: string) => request<void>(`/api/admin/skills/${id}`, { method: "DELETE" }),
};
